import { useEffect, useMemo, useState } from "react"
import { useSearch } from "@tanstack/react-router"
import { z } from "zod"

import { cn } from "@productlane/lib/src/cn"
import {
	Button,
	Form,
	FormInput,
	GoogleIcon,
	LinearIcon,
	ProductlaneIcon,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"

import { MetaInfo } from "@/components/meta-info"
import {
	EMAIL_MAXIMUM_LENGTH,
	EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE,
} from "@/constants"
import { env } from "@/env"
import { api } from "@/lib/api"
import { capturePendingSiteImport } from "@/lib/pending-site-import"
import { TermsNotice } from "../terms-notice"

const EmailLoginSchema = z.object({
	email: z
		.string()
		.email()
		.max(EMAIL_MAXIMUM_LENGTH, EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE),
})

export function LoginScreen() {
	const [isLoading, setIsLoading] = useState<
		"google" | "linear" | "email" | null
	>(null)
	const [emailStep, setEmailStep] = useState<"login" | "input" | "sent">(
		"login",
	)
	const [visibleStep, setVisibleStep] = useState<"login" | "input" | "sent">(
		"login",
	)
	const [isLeaving, setIsLeaving] = useState(false)
	const [sentToEmail, setSentToEmail] = useState("")
	const redirectUrl = useSearch({
		strict: false,
		select: (s) => s.redirectUrl,
	})

	// The marketing hero sends the help center address along; onboarding picks it up
	// after the OAuth roundtrip has dropped the query string.
	useEffect(() => {
		capturePendingSiteImport(window.location.search)
	}, [])

	useEffect(() => {
		if (emailStep === visibleStep) return
		setIsLeaving(true)
		const timeout = setTimeout(() => {
			setVisibleStep(emailStep)
			setIsLeaving(false)
		}, 200)
		return () => clearTimeout(timeout)
	}, [emailStep, visibleStep])

	const form = useZodForm({
		schema: EmailLoginSchema,
		defaultValues: { email: "" },
	})

	const emailLoginMutation = api.auth.sendLoginEmail.useMutation()

	const handleLogin = (type: "google" | "linear") => {
		setIsLoading(type)
		// encodeURIComponent so a redirectUrl that itself has query params (e.g.
		// the OAuth consent URL) isn't split on its inner `&` by the next handler.
		const target = redirectUrl ?? `${window.location.origin}/threads`
		window.location.href = `${env.VITE_API_URL}/api/auth/${type}?redirectUrl=${encodeURIComponent(
			target,
		)}`
	}

	const mainText = useMemo(() => {
		switch (visibleStep) {
			case "login":
				return "Productlane"
			case "input":
				return "What's your email?"
			case "sent":
				return "Check your email"
		}
	}, [visibleStep])

	return (
		<div className="flex max-h-dvh flex-1 flex-col overflow-hidden bg-gray-25 lg:flex-row dark:bg-[#0F0F12]">
			<MetaInfo title="Login" />
			<div className="z-1 flex flex-1 flex-col items-center justify-end p-6">
				<div className="flex flex-1 items-center">
					<div className="flex max-h-fit flex-col items-center rounded-3xl px-4 lg:w-screen lg:max-w-lg lg:p-14">
						<div className="login-rise-in mt-16 flex flex-col items-center">
							<div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#191A1C] ring-1 ring-white/10">
								<ProductlaneIcon fill="white" className="h-10" />
							</div>
						</div>
						<div className="login-rise-in min-h-72">
							<div
								className="login-step"
								data-leaving={isLeaving ? "true" : "false"}
							>
								<div className="text-center">
									<div className="cursor-default select-none text-3xl font-bold">
										{mainText}
									</div>
									<div
										className={cn(
											"mb-9 mt-1 text-center text-base font-medium text-secondary",
											{
												hidden: visibleStep !== "login",
											},
										)}
									>
										Sign up or log in to your account
									</div>
								</div>
								{visibleStep === "login" && (
									<div className="flex w-full max-w-[26.5rem] flex-col items-center gap-3">
										<Button
											Icon={LinearIcon}
											onClick={() => handleLogin("linear")}
											isLoading={isLoading === "linear"}
											variant="default"
											size="xl"
											className="w-full"
										>
											Continue with Linear
										</Button>

										<Button
											Icon={GoogleIcon}
											variant="outline"
											onClick={() => handleLogin("google")}
											isLoading={isLoading === "google"}
											size="xl"
											className="w-full"
										>
											Continue with Google
										</Button>

										<Button
											variant="outline"
											onClick={() => setEmailStep("input")}
											size="xl"
											className="w-full"
										>
											Continue with Email
										</Button>
									</div>
								)}

								{visibleStep === "input" && (
									<Form
										className="mt-6 flex w-full flex-col gap-2"
										id="email-login-form"
										form={form}
										onSubmit={async (values) => {
											setIsLoading("email")
											await emailLoginMutation.mutateAsync({
												...values,
												...(redirectUrl ? { redirectUrl } : {}),
											})
											setSentToEmail(values.email)
											setEmailStep("sent")
											setIsLoading(null)
										}}
									>
										<>
											<FormInput
												showErrorMessage={false}
												placeholder="email@example.com"
												name="email"
												disabled={isLoading === "email"}
												form="email-login-form"
												type="text"
												inputClassName="w-full !h-10"
												autoComplete="email"
												allowPasswordManager
											/>

											<SubmitButton
												form="email-login-form"
												isLoading={isLoading === "email"}
												className="mb-4 w-full"
												size="lg"
											>
												Continue with email
											</SubmitButton>

											<Button
												variant="link"
												onClick={() => setEmailStep("login")}
											>
												Back to login
											</Button>
										</>
									</Form>
								)}

								{visibleStep === "sent" && (
									<div className="mt-5 flex w-full flex-col items-center gap-4 text-center text-secondary">
										<p className="max-w-xs text-balance break-words text-sm">
											We sent a login link to{" "}
											<span className="font-medium text-primary">
												{sentToEmail}
											</span>
											. It works for 10 minutes.
										</p>

										<Button
											variant="outline"
											onClick={() => setEmailStep("login")}
										>
											Back to login
										</Button>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="login-rise-in">
					<TermsNotice />
				</div>
			</div>
		</div>
	)
}
